import { Combobox } from '@headlessui/react'
import { useEffect, useState } from 'react'

import { getStoryblokApi } from '@/lib/storyblok-api'

import styles from './Adviserinputsearch.module.css'

interface AdviserResult {
  id: string
  name: string
  givenName?: string
  surname?: string
  postcode?: string
  office: string
  url: string
}

interface StoryblokStory {
  id: string
  name: string
  full_slug: string
  content: {
    hideFromSearch?: boolean
    givenName?: string
    surname?: string
    postcode?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

interface StoryblokResponse {
  stories: StoryblokStory[]
}

export type AdviserinputsearchProps = {
  placeholder?: string
  width?: string | null
  variant?: 'primary' | 'secondary'
  hardCodedResults?: AdviserResult[]
  onSelect?: (item: AdviserResult | null) => void
  selectValue?: string
  onReset?: () => void
}

function PhnxAdviserinputsearch({
  placeholder = 'Enter a postcode or city name',
  width = '100%',
  variant = 'primary',
  hardCodedResults = [],
  onSelect,
}: AdviserinputsearchProps) {
  const [results, setResults] = useState<AdviserResult[]>([])
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedAdviser, setSelectedAdviser] = useState<AdviserResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const contentVersion = (process.env.NEXT_PUBLIC_SB_VERSION ?? 'published') as
    | 'published'
    | 'draft'

  // Debounce the query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query])

  // Search advisers based on debounced query
  useEffect(() => {
    if (hardCodedResults.length > 0) {
      setResults(hardCodedResults)
      return
    }

    const trimmedQuery = debouncedQuery.trim()
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setResults([])
      return
    }

    const searchAdvisers = async () => {
      try {
        setIsLoading(true)
        const location = process.env.NEXT_PUBLIC_NEXT_LOCATION ?? 'au'

        const sbParams = {
          search_term: debouncedQuery,
          filter_query: {
            'settings.location_available': { in: location },
            component: { in: 'adviser' },
            // Hide profiles that should not appear in adviser search.
            hideFromSearch: { not_in: 'true' },
          },
          resolve_relations:
            'article.author,article.relatedArticles,business_story.author,relatedContent.items,service.relatedArticles,adviser.offices,office.advisers,office.relatedAdvisers',
          per_page: 100,
          version: contentVersion,
        }

        const storyblokApi = getStoryblokApi()
        const { data }: { data: StoryblokResponse } = await storyblokApi.get(
          `cdn/stories/`,
          sbParams
        )

        const searchResults: AdviserResult[] =
          data?.stories != null
            ? data.stories
                .filter((story) => story.content?.hideFromSearch !== true)
                .map(
                  (story): AdviserResult => ({
                    id: story.id,
                    name: story.name,
                    givenName: story.content?.givenName,
                    surname: story.content?.surname,
                    postcode: story.content?.postcode,
                    office: story.name,
                    url: '/' + story.full_slug,
                  })
                )
                // Additional client-side filtering for better name matching
                .filter((adviser) => {
                  const queryLower = debouncedQuery.toLowerCase()
                  const givenNameLower = adviser.givenName?.toLowerCase() ?? ''
                  const surnameLower = adviser.surname?.toLowerCase() ?? ''
                  const nameLower = adviser.name.toLowerCase()

                  return (
                    givenNameLower.includes(queryLower) ||
                    surnameLower.includes(queryLower) ||
                    nameLower.includes(queryLower)
                  )
                })
                .sort((a, b) => {
                  const queryLower = debouncedQuery.toLowerCase()
                  const aGivenLower = a.givenName?.toLowerCase() ?? ''
                  const bGivenLower = b.givenName?.toLowerCase() ?? ''
                  const aNameLower = a.name.toLowerCase()
                  const bNameLower = b.name.toLowerCase()

                  // Exact match with givenName gets highest priority
                  if (aGivenLower === queryLower && bGivenLower !== queryLower) return -1
                  if (bGivenLower === queryLower && aGivenLower !== queryLower) return 1

                  // Then prioritize givenName that starts with query
                  if (aGivenLower.startsWith(queryLower) && !bGivenLower.startsWith(queryLower))
                    return -1
                  if (bGivenLower.startsWith(queryLower) && !aGivenLower.startsWith(queryLower))
                    return 1

                  // Then prioritize name that starts with query
                  if (aNameLower.startsWith(queryLower) && !bNameLower.startsWith(queryLower))
                    return -1
                  if (bNameLower.startsWith(queryLower) && !aNameLower.startsWith(queryLower))
                    return 1

                  // Otherwise maintain alphabetical order by name
                  return aNameLower.localeCompare(bNameLower)
                })
                .slice(0, 25)
            : []

        setResults(searchResults)
      } catch (error) {
        console.error('Error searching advisers:', error)
        setResults([])
      } finally {
        setIsLoading(false)
      }
    }

    searchAdvisers()
  }, [debouncedQuery, contentVersion])

  const handleOnSelect = (item: AdviserResult | null) => {
    if (item && onSelect) {
      onSelect(item)
    } else if (item) {
      window.location.href = item.url
    }
  }

  const handleClear = () => {
    setQuery('')
    setResults([])
    setSelectedAdviser(null)
  }

  // Clear when selected adviser changes (when clicking off or selecting)
  useEffect(() => {
    if (selectedAdviser !== null) {
      setQuery('')
      setResults([])
    }
  }, [selectedAdviser])

  return (
    <div className={`${styles['officeinputsearch']} ${styles[`variant-${variant}`]}`}>
      <div style={{ width: width ?? '100%' }} className="relative">
        <Combobox
          value={selectedAdviser}
          onChange={(value) => {
            setSelectedAdviser(value)
            handleOnSelect(value)
          }}
        >
          <div className="relative">
            <Combobox.Input
              className={styles.search}
              placeholder={placeholder}
              onChange={(event) => setQuery(event.target.value)}
              displayValue={(adviser: AdviserResult | null) => adviser?.name ?? ''}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            {query.trim() && (
              <Combobox.Options className={styles.options}>
                {isLoading ? (
                  <div className={styles.option}>
                    <span className={styles.result}>Searching...</span>
                  </div>
                ) : results.length > 0 ? (
                  results.map((item) => (
                    <Combobox.Option
                      key={item.id}
                      value={item}
                      className={`${styles.option} data-[selected]:${styles.optionActive}`}
                    >
                      <span className={styles.result}>{item.name}</span>
                    </Combobox.Option>
                  ))
                ) : (
                  <div className={styles.option}>
                    <span className={styles.result}>No advisers found</span>
                  </div>
                )}
              </Combobox.Options>
            )}
          </div>
        </Combobox>
        {query.trim() && isFocused && (
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer hover:opacity-70"
            onClick={handleClear}
            style={{ zIndex: 1000 }}
            aria-label="Clear search"
          >
            <svg
              width="20"
              height="20"
              focusable="false"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              style={{ fill: 'white' }}
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.58 12 5 17.58 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

export { PhnxAdviserinputsearch }
export default PhnxAdviserinputsearch
